import * as Schema from "effect/Schema";

import { PortSchema, PositiveInt, TrimmedNonEmptyString } from "./baseSchemas.ts";

export const DesktopBackendBootstrap = Schema.Struct({
  mode: Schema.Literal("desktop"),
  noBrowser: Schema.Boolean,
  port: PortSchema,
  // Omitted lets the server resolve its own home directory.
  cleoHome: Schema.optional(Schema.String),
  host: Schema.String,
  desktopBootstrapToken: Schema.String,
  // A fresh, non-credential identity for this child start. The public metadata
  // response echoes it so another server on the same port cannot make this Desk ready.
  desktopLaunchId: Schema.optionalKey(TrimmedNonEmptyString),
  // True only on the first start of this backend in a Desk launch (the Desk
  // counts its starts; no clock). The server then revokes the sessions earlier
  // launches' tokens were exchanged for, before it serves any request.
  // Absent or false (restarts, dev, headless, CLI): nothing is revoked.
  desktopBootstrapFirstStartOfLaunch: Schema.optionalKey(Schema.Boolean),
  otlpTracesUrl: Schema.optional(Schema.String),
  otlpMetricsUrl: Schema.optional(Schema.String),
  desktopTelemetryFd: Schema.optionalKey(PositiveInt),
  desktopTelemetryControlFd: Schema.optionalKey(PositiveInt),
  localModelEnginePath: Schema.optionalKey(TrimmedNonEmptyString),
  resourceMonitorPath: Schema.optionalKey(TrimmedNonEmptyString),
  // The key that seals the person's World sign-in at rest (base64, 32 bytes).
  // The Desk keeps it sealed by Electron safeStorage and hands it over only in
  // this envelope; the server never writes it anywhere (W-ba19ae23).
  worldSessionKey: Schema.optionalKey(Schema.String),
});

export type DesktopBackendBootstrap = typeof DesktopBackendBootstrap.Type;

export const DESKTOP_BACKEND_LAUNCH_HEADER = "x-cleo-desktop-launch-id";

/**
 * The exit code the local server uses when its database records a migration
 * newer than this build knows: a newer Cleo already used this data. The server
 * stops before running any migration or query, so the data is left exactly as
 * the newer build wrote it, and the Desk stops restarting it and explains
 * (W-f049fa90). 65 is EX_DATAERR from sysexits(3).
 */
export const DESKTOP_BACKEND_NEWER_DATA_EXIT_CODE = 65;

/**
 * How long the Desk gives the local server to stop after it signals it
 * (SIGTERM), before it forces it (SIGKILL). The server's own stop deadline
 * (below) must stay under this, so the server says what it is still waiting
 * for and exits before the Desk forces it (W-c855d6f6).
 */
export const DESKTOP_BACKEND_STOP_GRACE_MS = 15_000;

/**
 * How long the local server lets its own stop run once told to stop, whoever
 * told it (the Desk, the idle timer, a signal sent straight to the process).
 * Past this it logs the steps still running and exits with
 * DESKTOP_BACKEND_STOP_DEADLINE_EXIT_CODE. Under the Desk's grace by 3 s so
 * the log line is written before the Desk could force the process.
 */
export const DESKTOP_BACKEND_STOP_DEADLINE_MS = 12_000;

/** When a stop is still running, the server first says so, naming the steps. */
export const DESKTOP_BACKEND_STOP_PROGRESS_LOG_MS = 5_000;

/**
 * The exit code of a local server that did not finish stopping by its own
 * deadline. 69 is EX_UNAVAILABLE from sysexits(3).
 */
export const DESKTOP_BACKEND_STOP_DEADLINE_EXIT_CODE = 69;
